import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveViewerRole } from './callOffViewerRole';
import { warnAllocationShortfalls } from './allocationShortfallToast';
import { toast } from 'sonner';
import { useCallOffDetailModalData } from './useCallOffDetailModalData';
import { useCallOffInlineEdit } from './useCallOffInlineEdit';
import CallOffDetailModalTabs from './CallOffDetailModalTabs';
import CallOffDetailModalFooter from './CallOffDetailModalFooter';
import CallOffStatusBadges from '../CallOffStatusBadges';

interface CallOffDetailModalProps {
  open: boolean;
  callOffId: number | null;
  onClose: () => void;
  onUpdated: () => void;
  /** Called when customer wants to open wizard in edit mode */
  onEditInWizard?: (callOff: CallOff) => void;
}

export default function CallOffDetailModal({ open, callOffId, onClose, onUpdated, onEditInWizard }: CallOffDetailModalProps) {
  const { user } = useAuth();
  // CR-7732: the role comes from THIS call-off's parties, not the viewer's
  // company type — a supplier that also carries is the supplier on its own
  // loads and the haulier on the ones it hauls.
  const [viewerRole, setViewerRole] = useState(() => resolveViewerRole(null, user));
  const { isCustomer, isSupplier, isHaulier } = viewerRole;

  const {
    callOff, setCallOff, timeline, editHistory, messages, loading,
    asnTabCount, setAsnTabCount, fetchDetail,
    newMessage, setNewMessage, sendingMessage, handleSendMessage,
    markMessagesRead, unreadCount,
  } = useCallOffDetailModalData({ open, callOffId, isCustomer, onUpdated });

  useEffect(() => {
    setViewerRole(resolveViewerRole(callOff, user));
  }, [callOff, user]);

  const [actionLoading, setActionLoading] = useState(false);
  // CR-7781: the Haulage block reports unsaved edits so the footer's Confirm
  // Load cannot confirm on values the haulier has not saved yet.
  const [haulageDirty, setHaulageDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('detail');

  // Inline editing state (per-field, for customer)
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const {
    inlineEditMode, inlineEditChanges, savingBatchEdit,
    showAddParts, setShowAddParts, availableParts, availableDeliveries,
    selectedDelivery, setSelectedDelivery, addPartsLoading, partQuantities,
    setPartQuantities, addingPart, overByPart, overLineCount, hasOverLimit,
    enterInlineEditMode, cancelInlineEditMode, onQuantityChange, removeLineItem,
    saveBatchInlineEdit, handleAddPart, resetInlineEdit,
  } = useCallOffInlineEdit({ callOffId, callOff, fetchDetail, onUpdated, setActiveTab });

  useEffect(() => {
    if (open && callOffId) {
      setActiveTab('detail');
      setEditingLineId(null);
      resetInlineEdit();
    }
  }, [open, callOffId, resetInlineEdit]);

  // Close on Escape (parity with PO modal)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleAction = async (action: 'send' | 'confirm' | 'dispatch' | 'cancel', skipValidation = false, reason?: string) => {
    if (!callOffId) return;
    setActionLoading(true);
    try {
      const actionMap = {
        send: () => callOffAPI.sendCallOff(callOffId, skipValidation),
        confirm: () => callOffAPI.confirmCallOff(callOffId),
        dispatch: () => callOffAPI.dispatchCallOff(callOffId),
        cancel: () => callOffAPI.cancelCallOff(callOffId, reason),
      };
      const res = await actionMap[action]();
      setCallOff(res.data);
      const labels = { send: 'sent', confirm: 'confirmed', dispatch: 'dispatched', cancel: 'cancelled' };
      toast.success(`Call-off ${labels[action]} successfully`);
      // Confirm allocates the requested qty across open POs. If capacity was
      // lost between raising and confirming, say so — the delivery lines cover
      // less than was asked for, and silently shipping short is what this
      // message exists to prevent.
      if (action === 'confirm') warnAllocationShortfalls(res.data.allocation_shortfalls);
      onUpdated();
      fetchDetail();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string; message?: string } } };
      if (action === 'send' && axiosErr.response?.data?.code === 'insufficient_po_coverage') {
        const override = window.confirm(
          `${axiosErr.response.data.message || 'Insufficient PO coverage.'}\n\nDo you want to send anyway without PO validation?`
        );
        if (override) {
          setActionLoading(false);
          handleAction('send', true);
          return;
        }
      } else {
        const message = axiosErr.response?.data?.message || (err instanceof Error ? err.message : `Failed to ${action} call-off`);
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Per-field inline edit (customer)
  const startLineEdit = (lineId: number, field: string, currentValue: string) => {
    if (inlineEditMode) return;
    setEditingLineId(lineId);
    setEditingField(field);
    setEditValue(currentValue);
  };

  const cancelLineEdit = () => {
    setEditingLineId(null);
    setEditingField(null);
    setEditValue('');
  };

  const saveLineEdit = async () => {
    if (!callOffId || editingLineId === null || !editingField) return;
    setSavingEdit(true);
    try {
      const res = await callOffAPI.inlineEdit(callOffId, editingLineId, editingField, editValue);
      toast.success('Updated');
      warnAllocationShortfalls(res.data.allocation_shortfalls);
      cancelLineEdit();
      fetchDetail();
      onUpdated();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'communication' && messages.length > 0) {
      markMessagesRead();
    }
  }, [activeTab, markMessagesRead, messages.length]);

  if (!open) return null;

  const canEdit = callOff && ['draft', 'pending', 'confirmed'].includes(callOff.detail_status);
  const supplierCanEditLines = isSupplier && ['pending', 'confirmed'].includes(callOff?.detail_status || '');
  const changeCount = Object.keys(inlineEditChanges).length;

  // CR-6291: prompt (in the footer) to acknowledge any live ASN the customer
  // hasn't signed off yet, with a button that opens THAT specific ASN. A draft
  // ('created') or cancelled ASN can't be acknowledged, so those are excluded.
  const pendingAckAsns = (callOff?.asns || []).filter(
    a => !a.acknowledged && a.status !== 'created' && a.status !== 'cancelled',
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px', minWidth: '1200px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-primary">
              Call-Off: {callOff?.reference || '...'}
            </h2>
            {/* CR-7781: the same badges, and the same wording, as the list. */}
            {callOff && (
              <CallOffStatusBadges
                status={callOff.detail_status}
                logisticsConfirmedAt={callOff.logistics_confirmed_at}
              />
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading || !callOff ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <CallOffDetailModalTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              callOff={callOff}
              timeline={timeline}
              editHistory={editHistory}
              asnTabCount={asnTabCount}
              setAsnTabCount={setAsnTabCount}
              messages={messages}
              unreadCount={unreadCount}
              inlineEditMode={inlineEditMode}
              inlineEditChanges={inlineEditChanges}
              editingLineId={editingLineId}
              editingField={editingField}
              editValue={editValue}
              savingEdit={savingEdit}
              supplierCanEditLines={supplierCanEditLines}
              onQuantityChange={onQuantityChange}
              onStartLineEdit={startLineEdit}
              onSaveLineEdit={saveLineEdit}
              onCancelLineEdit={cancelLineEdit}
              setEditValue={setEditValue}
              removeLineItem={removeLineItem}
              showAddParts={showAddParts}
              availableParts={availableParts}
              availableDeliveries={availableDeliveries}
              selectedDelivery={selectedDelivery}
              setSelectedDelivery={setSelectedDelivery}
              addPartsLoading={addPartsLoading}
              partQuantities={partQuantities}
              setPartQuantities={setPartQuantities}
              addingPart={addingPart}
              handleAddPart={handleAddPart}
              isSupplier={isSupplier}
              overByPart={overByPart}
              isCustomer={isCustomer}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              handleSendMessage={handleSendMessage}
              sendingMessage={sendingMessage}
              onRefresh={fetchDetail}
              isHaulier={isHaulier}
              onHaulageDirtyChange={setHaulageDirty}
            />
          )}
        </div>

        {/* Footer — ASN acknowledge prompt (CR-6291), then workflow actions left, Close right */}
        <CallOffDetailModalFooter
          pendingAckAsns={pendingAckAsns}
          onClose={onClose}
          inlineEditMode={inlineEditMode}
          saveBatchInlineEdit={saveBatchInlineEdit}
          savingBatchEdit={savingBatchEdit}
          changeCount={changeCount}
          hasOverLimit={hasOverLimit}
          overLineCount={overLineCount}
          cancelInlineEditMode={cancelInlineEditMode}
          showAddParts={showAddParts}
          setShowAddParts={setShowAddParts}
          callOff={callOff}
          isCustomer={isCustomer}
          isSupplier={isSupplier}
          actionLoading={actionLoading}
          canEdit={Boolean(canEdit)}
          supplierCanEditLines={supplierCanEditLines}
          onEditInWizard={onEditInWizard}
          onAction={handleAction}
          onEnterInlineEdit={enterInlineEditMode}
          haulageDirty={haulageDirty}
          onLoadConfirmed={() => { fetchDetail(); onUpdated(); }}
        />
      </div>
    </div>
  );
}
